<template>
  <LoginLayout>
    <h1 class="pb-0h">{{ $t('auth.login.title') }}</h1>
    <form class="form" @submit.prevent="handleLogin">
      <TextInput
        v-model.trim="email"
        :label="$t('common.email')"
        placeholder=" "
        type="email"
      />
      <TextInput
        v-model="password"
        :type="showPassword ? 'text' : 'password'"
        :label="$t('common.password')"
        placeholder=" "
      >
        <template #append>
          <div
            class="showPassword"
            @click="togglePassword"
          >
            <IconComponent v-if="showPassword" icon="mdi-eye-off" size="1.5rem" />
            <IconComponent v-else icon="mdi-eye" size="1.5rem" />
          </div>
        </template>
      </TextInput>
      <p
        v-if="appLabelConfig.auth.showPasswordReset"
        class="text-right"
      >
        <a href="#" @click.prevent="openResetPassword">
          <strong>{{ $t('auth.login.forgottenPasswordLink') }}</strong>
        </a>
      </p>
      <div v-else>&nbsp;</div>
      <Btn
        :disabled="!email || !password"
        primary
        large
        block
        :loading="loading"
        @click.prevent="handleLogin"
      >
        {{ $t('auth.login.action') }}
      </Btn>
    </form>
    <div
      v-if="appLabelConfig.poolLiveFront.showDemo || appLabelConfig.auth.showRegistration"
    >
      <hr>
    </div>
    <div class="no-account-options">
      <div v-if="appLabelConfig.auth.showRegistration">
        <Btn block secondary large outline @click.prevent="createAccount">
          {{ $t('auth.login.createNewAccount') }}
        </Btn>
      </div>
      <div
        v-if="appLabelConfig.poolLiveFront.showDemo && appLabelConfig.auth.showRegistration"
        class="mt-1 mb-1 text-center"
      >
        {{ $t('common.or') }}
      </div>
      <Btn
        v-if="appLabelConfig.poolLiveFront.showDemo"
        block
        secondary
        large
        outline
        @click.prevent="tryDemo"
      >
        {{ $t('auth.login.tryDemo') }}
      </Btn>
    </div>
  </LoginLayout>
</template>

<script>
import TextInput from '@acs/components-library/src/components/common/Input.vue';
import Btn from '@acs/components-library/src/components/common/Btn.vue';
import LoginLayout from '../layouts/LoginLayout.vue';
import { startDemo } from '@acs/components-library/src/demo.js';
import * as PoolLiveDemo from '@acs/components-library/src/gql/live/Demo.js';
import { login } from '@acs/components-library/src/auth.js';
import IconComponent from '@acs/components-library/src/components/common/Icon.vue';
import { appLabelConfig } from '@acs/components-library/src/appLabel/appLabelConfig.js';
import { useFeatureFlagsStore } from '@acs/components-library/src/stores/featureFlagsStore';

const {
  VITE_RESET_PASSWORD_URL,
  VITE_CREATE_ACCOUNT_URL,
  VITE_TEST_USER_EMAIL,
  VITE_TEST_USER_PASSWORD,
} = import.meta.env;

// Only same-origin paths; `//host` and `/\host` are browser-resolvable as external URLs.
function isSafeRedirectPath(path) {
  return path.startsWith('/')
    && !path.startsWith('//')
    && !path.startsWith('/\\');
}

export default {
  name: 'LoginPage',
  components: {
    LoginLayout,
    TextInput,
    Btn,
    IconComponent,
  },
  setup() {
    const featureFlagsStore = useFeatureFlagsStore();
    const { forceRefetch } = featureFlagsStore;
    return { forceRefetch };
  },
  data() {
    return {
      email: '',
      password: '',
      loading: false,
      showPassword: false,
      appLabelConfig: appLabelConfig,
    };
  },
  computed: {
    redirectPath() {
      const { redirect } = this.$route.query;

      if (typeof redirect !== 'string' || !isSafeRedirectPath(redirect)) {
        return null;
      }

      return redirect;
    },
  },
  created() {
    this.email = VITE_TEST_USER_EMAIL || '';
    this.password = VITE_TEST_USER_PASSWORD || '';
  },
  methods: {
    async handleLogin() {
      this.loading = true;
      const success = await login(this.email, this.password);

      if (success) {
        this.email = '';
        this.password = '';
        this.forceRefetch();
        this.$router.replace(this.redirectPath || { name: 'UnitList' });
      }
      this.loading = false;
    },
    async openResetPassword() {
      const url = `${VITE_RESET_PASSWORD_URL}?&appBackLink=poolLiveFront`;
      window.open(url, '_blank');
    },
    async createAccount() {
      const url = `${VITE_CREATE_ACCOUNT_URL}?&appBackLink=poolLiveFront`;
      window.open(url, '_blank');
    },
    tryDemo() {
      startDemo(
        PoolLiveDemo,
        appLabelConfig.poolLiveFront,
      );
      this.$router.replace({ name: 'UnitList' });
    },
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
  },
};
</script>

<style lang="scss" scoped>
.header-logo {
  margin-top: .1875rem;
  margin-bottom: .25rem;
  text-align: center;
}

.container {
  height: 80vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1rem;
  max-width: 30rem;
  padding: 3rem;
  margin: auto;
}

.form {
  margin: 2rem 0;
}

.showPassword {
  cursor: pointer;
  display: flex;
  align-items: center;
}
</style>
